import React, { useEffect, useState } from 'react';
import type { ModuleKey } from './moduleTitle';
import { SHELL_MODULE_REGISTRY, shellNavLabel } from './moduleTitle';
import { canOpenModule, hasModuleGrant } from './biModuleKeys';
import { canOpenAdmin } from './foundationFilters';
import { moduleAvatar } from './moduleAvatars';

type Locale = 'hr' | 'en';

/**
 * HRMS tema — lijevi sidebar nav (zamjena za topbar nav kad je app_theme='hrms').
 * Renda se SAMO uz <body data-theme="hrms">; sav layout/boje su u app.css [data-theme="hrms"].
 */
export function HrmsSidebar({ user, locale, activeModule, onNavigate }: {
    user: { allowed_modules?: string[]; name?: string; email?: string; role?: string };
    locale: Locale;
    activeModule: ModuleKey;
    onNavigate: (key: ModuleKey) => void;
}) {
    const [collapsed, setCollapsed] = useState(false);

    // Collapsed stanje na <body> da app-shell padding (CSS) zna suziti razmak.
    useEffect(() => {
        document.body.classList.toggle('hrms-collapsed', collapsed);
        return () => document.body.classList.remove('hrms-collapsed');
    }, [collapsed]);

    const items = SHELL_MODULE_REGISTRY.filter((e) => {
        if (e.key === 'knjigovodstvo') {
            return (user.email ?? '').toLowerCase() === 'vjakomin@gmail.com';
        }
        if (e.key === 'poslovnice') {
            return user.role === 'administrator' || user.role === 'superadministrator';
        }
        if (e.key === 'admin') return canOpenAdmin({ ...user, role: user.role ?? '' }) && canOpenModule(user, 'admin');
        const canOpenThis = canOpenModule(user, e.key);
        if (!e.hiddenFromNav) return canOpenThis;
        if (e.key === activeModule) return true;
        if (!e.parentKey || !hasModuleGrant(user, e.key)) return false;
        return !canOpenModule(user, e.parentKey);
    });

    // Aktivni modul uvijek dostupan u dropdownu (i kad je hiddenFromNav podmodul) da select ne bude prazan.
    const activeEntry = SHELL_MODULE_REGISTRY.find((e) => e.key === activeModule);
    const mobileOptions = activeEntry && ! items.some((e) => e.key === activeModule) ? [activeEntry, ...items] : items;

    return (
        <>
        {/* Mobilna navigacija — dropdown dostupnih modula; fiksni sidebar je skriven na malim ekranima (vidi app.css). */}
        <div className="hrms-mobilebar">
            <img src="/aurodomus-logo.png" alt="Auro Domus" className="hrms-mobilebar-logo" />
            <select className="hrms-mobilebar-select" aria-label={locale === 'en' ? 'Module' : 'Modul'}
                value={activeModule} onChange={(ev) => onNavigate(ev.target.value as ModuleKey)}>
                {mobileOptions.map((en) => <option key={en.key} value={en.key}>{shellNavLabel(en, locale)}</option>)}
            </select>
        </div>
        <aside className={`hrms-sidebar${collapsed ? ' is-collapsed' : ''}`}>
            <div className="hrms-sidebar-head">
                <img src="/aurodomus-logo.png" alt="Auro Domus" className="hrms-logo" />
                <button type="button" className="hrms-collapse" onClick={() => setCollapsed((c) => !c)}
                    title={locale === 'en' ? 'Collapse' : 'Skupi'} aria-label="toggle sidebar">☰</button>
            </div>

            <nav className="hrms-nav">
                <div className="hrms-nav-section">{locale === 'en' ? 'MENU' : 'IZBORNIK'}</div>
                {items.map((e) => {
                    const av = moduleAvatar(e.key);
                    const active = e.key === activeModule;
                    const label = shellNavLabel(e, locale);
                    return (
                        <button key={e.key} type="button"
                            className={`hrms-nav-item${active ? ' is-active' : ''}`}
                            onClick={() => onNavigate(e.key)} title={label}>
                            <span className="hrms-nav-icon" style={{ background: `linear-gradient(135deg, ${av.grad[0]}, ${av.grad[1]})` }}>{av.icon}</span>
                            <span className="hrms-nav-label">{label}</span>
                        </button>
                    );
                })}
            </nav>

            <div className="hrms-sidebar-foot">
                <div className="hrms-user-avatar">{(user.name || user.email || '?').slice(0, 1).toUpperCase()}</div>
                <div className="hrms-user-meta">
                    <div className="hrms-user-name">{user.name || user.email}</div>
                    <div className="hrms-user-role">{user.role || ''}</div>
                </div>
            </div>
        </aside>
        </>
    );
}
